/**
 * Pipeline API Service
 *
 * Thin wrapper around HTTP calls for the Requirements → Deploy pipeline.
 * Uses the same agentCoreClient / lambdaClient as the rest of the app.
 */
import { agentCoreClient, lambdaClient } from '../config/http.client';

/**
 * Run a single pipeline stage via AgentCore.
 *
 * @param {string} stage - 'requirements' | 'design' | 'docs' | 'code' | 'tests' | 'cicd'
 * @param {object} inputData - stage-specific input (requirements text, previous-stage outputs, etc.)
 * @param {string} [comment] - optional user feedback / correction for this generation
 * @param {string} [modelId] - optional model override
 * @returns {{ jobId: string, status: string } | { result: object, status: 'completed' }}
 */
export const runPipelineStage = async (stage, inputData, comment = '', modelId = null) => {
  const body = {
    action: 'pipeline-stage',
    stage,
    input_data: inputData,
    user_comment: comment || '',
    model_id: modelId,
  };

  const response = await agentCoreClient.post('/agentcore/invoke', body);
  const data = response.data;

  if (data.status === 'accepted' && data.job_id) {
    return { jobId: data.job_id, status: 'accepted' };
  }

  // Synchronous result (local Flask dev server)
  if (data.result) {
    return { result: data.result, status: 'completed' };
  }

  throw new Error(data.error || `Failed to start pipeline stage: ${stage}`);
};

/**
 * Poll a pipeline job until it resolves.
 * Returns the raw job object from DynamoDB.
 */
export const getPipelineJobStatus = async (jobId) => {
  const response = await agentCoreClient.get(`/agentcore/jobs/${jobId}`);
  return response.data;
};

/**
 * Trigger an AWS deployment for the finished pipeline.
 * @param {object} payload - { requirements, design, docs, code, cicd }
 */
export const triggerDeployment = async (payload) => {
  const response = await lambdaClient.post('/pipeline/deploy', payload);
  return response.data;
};

/**
 * Get deployment job status.
 */
export const getDeploymentStatus = async (jobId) => {
  const response = await lambdaClient.get(`/pipeline/deploy/${jobId}`);
  return response.data;
};
